import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuBot, LuLink, LuLoaderPinwheel, LuSend, LuTrash2 } from 'react-icons/lu';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { StandaloneChangelogEntry } from '../../entities/standalone-changelog';
import { StandaloneComment } from '../../entities/standalone-comment';
import { StandaloneProject } from '../../entities/standalone-project';
import { StandaloneTask, StandaloneTaskMinimal, StandaloneTaskPriority } from '../../entities/standalone-task';
import {
  createStandaloneComment,
  createStandaloneTask,
  deleteStandaloneComment,
  deleteStandaloneTask,
  getStandaloneProject,
  getStandaloneTask,
  getStandaloneTaskByKey,
  listStandaloneChangelog,
  listStandaloneComments,
  listStandaloneSubtasks,
  listStandaloneTasksMinimal,
  standaloneErrorMessage,
  StandaloneTaskInput,
  updateStandaloneTask,
} from '../../shared/api/standalone';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { AgentRoleWarning, getAgentRoleWarning } from '../../shared/utils/agent-role';
import { contrastTextColor } from '../../shared/utils/color';
import { collectDescendantIds } from '../../shared/utils/standalone-task-tree';
import MarkdownRenderer from '../../widgets/MarkdownRenderer';
import { AgentRoleWarningModal } from '../../widgets/standalone/AgentRoleWarningModal';
import { AttachmentsPanel } from '../../widgets/standalone/AttachmentsPanel';
import { ChangelogFeed } from '../../widgets/standalone/ChangelogFeed';
import { TaskDetailShell } from '../../widgets/task-detail/TaskDetailShell';
import { AgentAuthorBadge } from '../../widgets/ui/AgentAuthorBadge';
import { MarkdownField } from '../../widgets/ui/MarkdownField';

// Matches widgets/standalone/TaskCard.tsx's board-card colors, for a consistent look between the
// board and this page's header strip.
const PRIORITY_BADGE: Record<string, string> = {
  high: 'badge-error',
  medium: 'badge-warning',
  low: 'badge-ghost',
};

// Full-page "work with this task more deeply" view, reached from TaskModal's "Open full page"
// button or a shared `/standalone/tasks/:key` link (standalone-contract.md §0 "Readable keys").
// Previously this route just redirected into the board + modal; it now renders the task directly.
const StandaloneTaskDetailPage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { on, off } = useSocket(auth.currentUser?.uid || '');

  const [task, setTask] = useState<StandaloneTask | null>(null);
  const [project, setProject] = useState<StandaloneProject | null>(null);
  // This task's direct children — shown immediately, so fetched eagerly alongside the task itself.
  const [subtasks, setSubtasks] = useState<StandaloneTask[]>([]);
  // The current parent's own title/key (a single cheap lookup), so "Parent task" can show it
  // without ever needing the full project task list just to display what's already set.
  const [parentTask, setParentTask] = useState<StandaloneTaskMinimal | null>(null);
  // The project-wide title/tree projection for the *picker* — only fetched on demand, the moment
  // the user actually opens it to change the parent, never on page load.
  const [parentCandidates, setParentCandidates] = useState<StandaloneTaskMinimal[]>([]);
  const [isEditingParent, setIsEditingParent] = useState(false);
  const [isLoadingParentCandidates, setIsLoadingParentCandidates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSavingText, setIsSavingText] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [agentRoleWarning, setAgentRoleWarning] = useState<AgentRoleWarning | null>(null);

  const [activityTab, setActivityTab] = useState<'comments' | 'activity' | 'attachments'>('comments');
  const [comments, setComments] = useState<StandaloneComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [changelog, setChangelog] = useState<StandaloneChangelogEntry[]>([]);
  const [isLoadingChangelog, setIsLoadingChangelog] = useState(false);

  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    getStandaloneTaskByKey(key)
      .then(async (t) => {
        if (cancelled) return;
        setTask(t);
        setTitle(t.title);
        setDescription(t.description ?? '');
        const [proj, subs, taskComments, parent] = await Promise.all([
          getStandaloneProject(t.projectId),
          listStandaloneSubtasks(t.projectId, t._id),
          listStandaloneComments(t._id),
          // A single cheap lookup instead of the full project task list, purely to show what's
          // already set — the picker itself only loads that list when actually opened.
          t.parentId ? getStandaloneTask(t.parentId).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setProject(proj);
        setSubtasks(subs);
        setComments(taskComments);
        setParentTask(parent);
      })
      .catch(() => {
        if (!cancelled) setLoadError(`No task found for "${key}".`);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  // Read-only, no live socket event in the BE contract (MVPB-25) — loaded once per task, kept
  // separate from the effect above so a changelog hiccup can't hold up the rest of the page.
  useEffect(() => {
    if (!task) return;
    setIsLoadingChangelog(true);
    listStandaloneChangelog(task._id)
      .then(setChangelog)
      .catch(() => {})
      .finally(() => setIsLoadingChangelog(false));
  }, [task]);

  // Live sync — the chat agent can also add comments via its own tool call.
  useEffect(() => {
    if (!task) return;
    const onCommentAdded = (data: { userId: string; comment: StandaloneComment }) => {
      if (data.comment.taskId !== task._id) return;
      setComments((prev) => (prev.some((c) => c._id === data.comment._id) ? prev : [...prev, data.comment]));
    };
    on('standaloneCommentAdded', onCommentAdded);
    return () => off('standaloneCommentAdded', onCommentAdded as (...args: unknown[]) => void);
  }, [task, on, off]);

  const statuses = useMemo(() => [...(project?.statuses ?? [])].sort((a, b) => a.order - b.order), [project]);

  const excludedParentIds = useMemo(() => {
    if (!task) return new Set<string>();
    return new Set([task._id, ...collectDescendantIds(task._id, parentCandidates)]);
  }, [task, parentCandidates]);

  const parentOptions = parentCandidates.filter((t) => !excludedParentIds.has(t._id));
  const isDirtyText = !!task && (title !== task.title || description !== (task.description ?? ''));

  // Instant-save for discrete fields (status/priority/due date/labels/parent).
  const saveField = async (patch: Partial<StandaloneTaskInput>) => {
    if (!task) return;
    setError(null);
    try {
      const updated = await updateStandaloneTask(task._id, patch);
      setTask(updated);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to update task.');
    }
  };

  // Fetches the project's title/tree projection only the moment the user actually opens the
  // picker — cached for the rest of this page visit so reopening it doesn't refetch.
  const handleOpenParentPicker = async () => {
    setIsEditingParent(true);
    if (!task || parentCandidates.length > 0) return;
    setIsLoadingParentCandidates(true);
    try {
      setParentCandidates(await listStandaloneTasksMinimal(task.projectId));
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to load tasks.');
    } finally {
      setIsLoadingParentCandidates(false);
    }
  };

  const handleChangeParent = async (parentId: string) => {
    if (!task) return;
    setError(null);
    try {
      const updated = await updateStandaloneTask(task._id, { parentId: parentId || undefined });
      setTask(updated);
      setParentTask(parentId ? (parentCandidates.find((t) => t._id === parentId) ?? null) : null);
      setIsEditingParent(false);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to update task.');
    }
  };

  const handleSaveText = async () => {
    if (!task || !title.trim()) return;
    setIsSavingText(true);
    setError(null);
    try {
      const updated = await updateStandaloneTask(task._id, {
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setTask(updated);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to save changes.');
    } finally {
      setIsSavingText(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    if (!window.confirm('Delete this task?')) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteStandaloneTask(task._id);
      navigate(`/standalone/${task.projectId}`);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete task.');
      setIsDeleting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!task) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const goToAgent = () => {
    if (!task) return;
    navigate('/coding-agent', {
      state: {
        standaloneTaskId: task._id,
        taskKey: task.key,
        prompt: task.description ? `${task.title}\n\n${task.description}` : task.title,
      },
    });
  };

  const handleAssignToAgent = () => {
    if (!task) return;
    if (project) {
      const warning = getAgentRoleWarning(project);
      if (warning) {
        setAgentRoleWarning(warning);
        return;
      }
    }
    goToAgent();
  };

  const toggleLabel = (labelId: string) => {
    if (!task) return;
    const current = task.labelIds ?? [];
    const next = current.includes(labelId) ? current.filter((id) => id !== labelId) : [...current, labelId];
    saveField({ labelIds: next });
  };

  const handleAddSubtask = async () => {
    if (!task || !subtaskTitle.trim()) return;
    setIsAddingSubtask(true);
    setError(null);
    try {
      const created = await createStandaloneTask(task.projectId, {
        title: subtaskTitle.trim(),
        status: statuses[0]?.id,
        parentId: task._id,
      });
      setSubtasks((prev) => [...prev, created]);
      setParentCandidates((prev) => [...prev, { _id: created._id, title: created.title, key: created.key }]);
      setSubtaskTitle('');
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to add subtask.');
    } finally {
      setIsAddingSubtask(false);
    }
  };

  const handleAddComment = async () => {
    if (!task || !commentText.trim()) return;
    setIsAddingComment(true);
    setError(null);
    try {
      const created = await createStandaloneComment(task._id, commentText.trim());
      setComments((prev) => (prev.some((c) => c._id === created._id) ? prev : [...prev, created]));
      setCommentText('');
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to add comment.');
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!task) return;
    setDeletingCommentId(commentId);
    setError(null);
    try {
      await deleteStandaloneComment(task._id, commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete comment.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  return (
    <>
      {agentRoleWarning && project && (
        <AgentRoleWarningModal
          projectId={project._id}
          warning={agentRoleWarning}
          onCancel={() => setAgentRoleWarning(null)}
          onContinue={() => {
            setAgentRoleWarning(null);
            goToAgent();
          }}
        />
      )}
      <TaskDetailShell
        backTo={task ? `/standalone/${task.projectId}` : '/standalone'}
        backLabel="Back to board"
        provider="standalone"
        titleKey={task?.key}
        isLoading={isLoading}
        error={loadError}
        title={
          project &&
          task && (
            <Link to={`/standalone/${task.projectId}`} className="hover:underline">
              {project.name}
            </Link>
          )
        }
        meta={
          task && (
            <>
              {(() => {
                const currentStatus = statuses.find((s) => s.id === task.status);
                return (
                  currentStatus && (
                    <span
                      className="badge badge-sm gap-1"
                      style={{
                        backgroundColor: currentStatus.color || '#9ca3af',
                        borderColor: currentStatus.color || '#9ca3af',
                        color: contrastTextColor(currentStatus.color),
                      }}
                    >
                      {currentStatus.name}
                    </span>
                  )
                );
              })()}
              {task.priority && (
                <span className={`badge badge-sm ${PRIORITY_BADGE[task.priority]}`}>{task.priority}</span>
              )}
              {task.dueDate && (
                <span className="text-xs opacity-50">Due {new Date(task.dueDate).toLocaleDateString()}</span>
              )}
            </>
          )
        }
        headerActions={
          task && (
            <>
              <button className="btn btn-ghost btn-sm gap-1" onClick={handleCopyLink} title="Copy link">
                <LuLink size={13} /> Copy link
              </button>
              <button
                className="btn btn-ghost btn-sm gap-1"
                onClick={handleAssignToAgent}
                title="Assign to coding agent"
              >
                <LuBot size={13} /> Assign to agent
              </button>
              <button
                className="btn btn-ghost btn-sm gap-1 text-error"
                onClick={handleDelete}
                disabled={isDeleting}
                title="Delete task"
              >
                {isDeleting ? <LuLoaderPinwheel className="animate-spin" size={14} /> : <LuTrash2 size={13} />}
                Delete
              </button>
            </>
          )
        }
        sidebar={
          task &&
          project && (
            <>
              <div className="card bg-base-100 shadow-card">
                <div className="card-body p-4 gap-3">
                  <label className="form-control">
                    <span className="label-text text-xs mb-1">Status</span>
                    <select
                      className="select select-bordered select-sm"
                      value={task.status}
                      onChange={(e) => saveField({ status: e.target.value })}
                    >
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="form-control">
                    <span className="label-text text-xs mb-1">Priority</span>
                    <select
                      className="select select-bordered select-sm"
                      value={task.priority ?? ''}
                      onChange={(e) => saveField({ priority: (e.target.value || undefined) as StandaloneTaskPriority })}
                    >
                      <option value="">None</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </label>

                  <label className="form-control">
                    <span className="label-text text-xs mb-1">Due date</span>
                    <input
                      type="date"
                      className="input input-bordered input-sm"
                      value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
                      onChange={(e) => saveField({ dueDate: e.target.value || undefined })}
                    />
                  </label>

                  <div className="form-control">
                    <span className="label-text text-xs mb-1">Parent task</span>
                    {isEditingParent ? (
                      isLoadingParentCandidates ? (
                        <div className="flex items-center gap-2 text-sm opacity-60 py-1">
                          <LuLoaderPinwheel className="animate-spin" size={14} />
                          Loading tasks…
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            className="select select-bordered select-sm flex-1 min-w-0"
                            value={task.parentId ?? ''}
                            onChange={(e) => handleChangeParent(e.target.value)}
                          >
                            <option value="">None (root task)</option>
                            {parentOptions.map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.title}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            onClick={() => setIsEditingParent(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      )
                    ) : (
                      <button
                        type="button"
                        className="flex items-center justify-between gap-2 rounded-lg bg-base-200 hover:bg-base-300 transition px-3 py-1.5 text-sm text-left"
                        onClick={handleOpenParentPicker}
                      >
                        <span className={parentTask ? '' : 'opacity-50'}>
                          {parentTask ? `${parentTask.key} — ${parentTask.title}` : 'None (root task)'}
                        </span>
                        <span className="text-xs opacity-50 shrink-0">Change</span>
                      </button>
                    )}
                  </div>

                  {project.labels.length > 0 && (
                    <div className="form-control">
                      <span className="label-text text-xs mb-1">Labels</span>
                      <div className="flex flex-wrap gap-1.5">
                        {project.labels.map((label) => {
                          const active = (task.labelIds ?? []).includes(label.id);
                          return (
                            <button
                              key={label.id}
                              type="button"
                              className={`badge badge-sm cursor-pointer ${active ? '' : 'badge-outline opacity-60'}`}
                              style={
                                active
                                  ? {
                                      backgroundColor: label.color || '#6b7280',
                                      borderColor: label.color || '#6b7280',
                                      color: contrastTextColor(label.color),
                                    }
                                  : undefined
                              }
                              onClick={() => toggleLabel(label.id)}
                            >
                              {label.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card bg-base-100 shadow-card">
                <div className="card-body p-4 gap-2">
                  <span className="label-text text-xs">Subtasks {subtasks.length > 0 && `(${subtasks.length})`}</span>
                  {subtasks.length > 0 && (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {subtasks.map((sub) => {
                        const subStatus = statuses.find((s) => s.id === sub.status);
                        return (
                          <button
                            key={sub._id}
                            className="w-full flex items-center gap-2 text-sm bg-base-200 rounded-lg px-2.5 py-1.5 text-left hover:bg-base-300 transition"
                            onClick={() => navigate(`/standalone/tasks/${sub.key}`)}
                          >
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: subStatus?.color || '#9ca3af' }}
                            />
                            <span className="font-mono text-xs opacity-50 shrink-0">{sub.key}</span>
                            <span className="truncate flex-1">{sub.title}</span>
                            <span className="text-xs opacity-60 shrink-0">{subStatus?.name || 'Unknown'}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      className="input input-bordered input-sm flex-1"
                      placeholder="Add a subtask…"
                      value={subtaskTitle}
                      onChange={(e) => setSubtaskTitle(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                    />
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={handleAddSubtask}
                      disabled={!subtaskTitle.trim() || isAddingSubtask}
                    >
                      {isAddingSubtask && <LuLoaderPinwheel className="animate-spin" size={14} />}
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </>
          )
        }
      >
        {task && (
          <>
            {error && (
              <div className="alert alert-error py-2">
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="card bg-base-100 shadow-card">
              <div className="card-body p-5 gap-3">
                {task.authorSource === 'mcp_agent' && (
                  <div>
                    <AgentAuthorBadge clientName={task.clientName} />
                  </div>
                )}
                <input
                  className="input input-bordered input-lg w-full text-base font-semibold"
                  placeholder="Task title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <MarkdownField
                  value={description}
                  onChange={setDescription}
                  placeholder="Description (optional) — Markdown supported"
                  rows={10}
                />
                {isDirtyText && (
                  <div className="flex justify-end gap-2">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setTitle(task.title);
                        setDescription(task.description ?? '');
                      }}
                      disabled={isSavingText}
                    >
                      Discard
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveText}
                      disabled={isSavingText || !title.trim()}
                    >
                      {isSavingText && <LuLoaderPinwheel className="animate-spin" size={14} />}
                      Save changes
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="card bg-base-100 shadow-card">
              <div className="card-body p-5 gap-3">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className={`btn btn-xs ${activityTab === 'comments' ? 'btn-active' : 'btn-ghost'}`}
                    onClick={() => setActivityTab('comments')}
                  >
                    Comments {comments.length > 0 && `(${comments.length})`}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-xs ${activityTab === 'activity' ? 'btn-active' : 'btn-ghost'}`}
                    onClick={() => setActivityTab('activity')}
                  >
                    Activity {changelog.length > 0 && `(${changelog.length})`}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-xs ${activityTab === 'attachments' ? 'btn-active' : 'btn-ghost'}`}
                    onClick={() => setActivityTab('attachments')}
                  >
                    Attachments
                  </button>
                </div>

                {activityTab === 'activity' ? (
                  <ChangelogFeed entries={changelog} isLoading={isLoadingChangelog} />
                ) : activityTab === 'attachments' ? (
                  <AttachmentsPanel taskId={task._id} />
                ) : (
                  <>
                    {comments.length > 0 && (
                      <div className="space-y-1.5">
                        {comments.map((comment) => (
                          <div key={comment._id} className="rounded-lg bg-base-200 px-3 py-2 text-sm space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5">
                                {comment.authorType === 'agent' && (
                                  <span className="badge badge-primary badge-xs gap-1">
                                    <LuBot size={10} /> mFlow
                                  </span>
                                )}
                                {comment.authorSource === 'mcp_agent' && (
                                  <AgentAuthorBadge clientName={comment.clientName} />
                                )}
                                <span className="text-xs opacity-40">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <button
                                className="btn btn-ghost btn-xs btn-circle"
                                onClick={() => handleDeleteComment(comment._id)}
                                disabled={deletingCommentId === comment._id}
                                title="Delete comment"
                              >
                                {deletingCommentId === comment._id ? (
                                  <span className="loading loading-spinner loading-xs" />
                                ) : (
                                  <LuTrash2 size={12} />
                                )}
                              </button>
                            </div>
                            <MarkdownRenderer markdown={comment.text} />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <MarkdownField
                        value={commentText}
                        onChange={setCommentText}
                        placeholder="Add a comment… (Markdown supported, ⌘+Enter to send)"
                        rows={2}
                        className="textarea textarea-bordered textarea-sm w-full"
                        onSubmitShortcut={handleAddComment}
                      />
                      <div className="flex justify-end">
                        <button
                          className="btn btn-outline btn-sm gap-1.5"
                          onClick={handleAddComment}
                          disabled={!commentText.trim() || isAddingComment}
                        >
                          {isAddingComment ? (
                            <LuLoaderPinwheel className="animate-spin" size={14} />
                          ) : (
                            <LuSend size={14} />
                          )}
                          Comment
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </TaskDetailShell>
    </>
  );
};

export default StandaloneTaskDetailPage;
